import {
  PlatformMark,
  safeHttpUrl,
} from "@/components/non_registrant/str-property-details";
import { Badge } from "@/components/ui/badge";
import type { LicenseStatus } from "@/hera-api/client/types.gen";
import { ALL_LICENSE_STATUSES } from "@/lib/enum-constants";
import {
  type StatusBadgeVariant,
  getRegistrationStatusDisplay,
} from "@/lib/utils";

const LICENSE_STATUS_LABELS = new Set<string>(ALL_LICENSE_STATUSES);

export function isLicenseStatusLabel(
  statusLabel: string,
): statusLabel is LicenseStatus {
  return LICENSE_STATUS_LABELS.has(statusLabel);
}

/** Unregistered and Multiple licenses stay literal. One license uses the list label. */
export function strStatusDisplayLabel(statusLabel: string): string {
  if (isLicenseStatusLabel(statusLabel)) {
    return getRegistrationStatusDisplay(statusLabel).label;
  }
  return statusLabel;
}

export function strStatusBadgeVariant(statusLabel: string): StatusBadgeVariant {
  if (isLicenseStatusLabel(statusLabel)) {
    return getRegistrationStatusDisplay(statusLabel).variant;
  }
  return "secondary";
}

export function StrPropertyPinCard({
  address,
  addressUnverified = false,
  statusLabel,
  platforms,
  heroImageUrl,
}: {
  address: string | null;
  addressUnverified?: boolean;
  statusLabel: string;
  platforms: string[];
  heroImageUrl: string | null;
}) {
  const hero = safeHttpUrl(heroImageUrl);
  return (
    <div className="space-y-2" data-testid="str-property-pin-card">
      {hero ? (
        <img
          src={hero}
          alt="Listing"
          className="h-32 w-full rounded border object-cover"
        />
      ) : null}
      <div className="flex items-start gap-4">
        {address ? (
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-gray-700">Address</p>
            <div className="text-sm text-gray-900">{address}</div>
            {addressUnverified ? (
              <p className="text-xs text-muted-foreground">
                Exact location. Address not verified.
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="flex-shrink-0">
          <p className="text-xs font-semibold text-gray-700">Status</p>
          <Badge
            variant={strStatusBadgeVariant(statusLabel)}
            className="text-sm"
          >
            {strStatusDisplayLabel(statusLabel)}
          </Badge>
        </div>
      </div>
      {platforms.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2"
          aria-label="Platforms"
        >
          {platforms.map((platform) => (
            <PlatformMark
              key={platform}
              platform={platform}
              className="h-8 w-8 object-contain"
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
